import { useEffect } from "react";

/* ============================================================
   O que faz o app não parecer um site

   Um PWA instalado continua sendo o Chrome desenhando a página, e o
   Chrome entrega de graça comportamentos que denunciam isso na hora:
   segurar um card do feed abre "copiar endereço do link", segurar a foto
   abre "baixar imagem". Nenhum app de loja faz isso. É o tipo de detalhe
   que ninguém sabe nomear — a pessoa só sente que está num site.

   Empacotar como TWA NÃO resolve. O TWA é o mesmo Chrome sem a barra de
   endereço: o menu de toque longo é do conteúdo, não da moldura. Some a
   barra, ficam os menus.

   O preço de tirar o menu é o "compartilhar imagem" que vinha junto —
   daí `compartilharFoto` aqui embaixo. Tirar o gesto sem repor a função
   seria trocar um incômodo por uma perda.
   ============================================================ */

/** Onde o toque longo continua valendo. */
const SELECIONAVEL = ".selecionavel";

/**
 * Desliga o menu de contexto do navegador no app inteiro.
 *
 * A exceção é texto que a pessoa tem motivo para copiar: comentário,
 * recado do bate-papo, descrição do prato. Bloquear ali trocaria "parece
 * um site" por "não dá para copiar" — que é pior, e que ninguém
 * relaciona com esta decisão depois.
 */
export function useSemMenuNativo(): void {
  useEffect(() => {
    const bloquear = (e: Event) => {
      const alvo = e.target as Element | null;
      if (alvo?.closest?.(SELECIONAVEL)) return;
      e.preventDefault();
    };
    document.addEventListener("contextmenu", bloquear);
    return () => document.removeEventListener("contextmenu", bloquear);
  }, []);
}

export const podeCompartilhar = (): boolean =>
  typeof navigator !== "undefined" && typeof navigator.share === "function";

/**
 * Compartilha a foto do prato pela folha nativa do sistema.
 *
 * Manda o ARQUIVO, não o link: o app é fechado por convite, então uma URL
 * de prato só abre para quem já está no desafio — mandar o link para o
 * grupo da família não mostra nada.
 *
 * Cai para texto quando o arquivo não vai. São dois motivos reais, e
 * nenhum dos dois dá para prever daqui: `canShare({ files })` é falso em
 * alguns navegadores, e a busca do blob depende de CORS no bucket. Falhar
 * abrindo a folha com o texto é melhor que um botão que não faz nada.
 */
export async function compartilharFoto(
  url: string | null, titulo: string, endereco: string,
): Promise<void> {
  const texto = { title: titulo || "Nhomi", text: titulo, url: endereco };

  if (url) {
    try {
      const resposta = await fetch(url);
      const blob = await resposta.blob();
      const arquivo = new File([blob], `${titulo || "prato"}.jpg`, {
        type: blob.type || "image/jpeg",
      });
      if (navigator.canShare?.({ files: [arquivo] })) {
        await navigator.share({ ...texto, files: [arquivo] });
        return;
      }
    } catch (err) {
      // Desistir do arquivo é normal; desistir de compartilhar não.
      if ((err as Error)?.name === "AbortError") return;
    }
  }

  try {
    await navigator.share(texto);
  } catch (err) {
    // Fechar a folha do sistema não é erro e não merece aviso na tela.
    if ((err as Error)?.name !== "AbortError") throw err;
  }
}
